// Labeled input with a hint and an optional error message. Used in the auth
// forms; keeps consistent spacing, focus ring, and aria-describedby wiring.

import { forwardRef, type InputHTMLAttributes } from "react";

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
}

let fieldCounter = 0;
function nextFieldId(): string {
  fieldCounter += 1;
  return `field-${fieldCounter}`;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, hint, error, id, className, ...rest },
  ref,
) {
  const inputId = id ?? nextFieldId();
  const describedBy: string[] = [];
  if (hint) describedBy.push(`${inputId}-hint`);
  if (error) describedBy.push(`${inputId}-error`);

  const inputClass =
    "block w-full rounded-md border bg-white px-3 py-2 text-sm shadow-sm " +
    "placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-0 " +
    (error
      ? "border-red-400 focus:border-red-500 focus:ring-red-200"
      : "border-slate-300 focus:border-indigo-500 focus:ring-indigo-200");

  return (
    <div className={`flex flex-col gap-1 ${className ?? ""}`.trim()}>
      <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        {...rest}
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy.length ? describedBy.join(" ") : undefined}
        className={inputClass}
      />
      {hint && !error && (
        <span id={`${inputId}-hint`} className="text-xs text-slate-500">
          {hint}
        </span>
      )}
      {error && (
        <span id={`${inputId}-error`} className="text-xs text-red-600">
          {error}
        </span>
      )}
    </div>
  );
});

export default FormField;
